import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Plus, Settings, Timer, Search, X, Users, ListPlus, Trash2, Pencil, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import KanbanColumn from '../components/KanbanColumn';
import KanbanTicketCard from '../components/KanbanTicketCard';
import CreateTicketModal from '../components/CreateTicketModal';
import TicketDetailModal from '../components/TicketDetailModal';
import AddColumnModal from '../components/AddColumnModal';


function KanbanBoard() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [columns, setColumns] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [boards, setBoards] = useState([]);
  const [users, setUsers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [originalColumnId, setOriginalColumnId] = useState(null);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColumnForTicket, setNewColumnForTicket] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isBoardAdmin, setIsBoardAdmin] = useState(false);
  
  // Settings state
  const [ttcStartColumn, setTtcStartColumn] = useState('');
  const [ttcEndColumn, setTtcEndColumn] = useState('');
  const [boardAdminUserId, setBoardAdminUserId] = useState('');
  
  // Custom fields state
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [newFieldMandatory, setNewFieldMandatory] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editFieldName, setEditFieldName] = useState('');
  const [editFieldOptions, setEditFieldOptions] = useState('');
  const [editFieldMandatory, setEditFieldMandatory] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Board filter state
  const [filterCreator, setFilterCreator] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterFieldId, setFilterFieldId] = useState('all');
  const [filterFieldValue, setFilterFieldValue] = useState('all');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const loadData = useCallback(async () => {
    try {
      const [boardRes, columnsRes, ticketsRes, boardsRes, usersRes, customFieldsRes, adminCheckRes] = await Promise.all([
        api.get(`/boards/${boardId}`),
        api.get(`/boards/${boardId}/columns`),
        api.get(`/tickets/board/${boardId}`),
        api.get(`/boards`),
        api.get(`/employees/active`),
        api.get(`/boards/${boardId}/custom-fields`),
        api.get(`/boards/${boardId}/check-admin`),
      ]);
      setBoard(boardRes.data);
      setColumns(columnsRes.data);
      setTickets(ticketsRes.data);
      setBoards(boardsRes.data);
      setUsers(usersRes.data);
      setCustomFields(customFieldsRes.data);
      setIsSuperAdmin(adminCheckRes.data.is_super_admin);
      setIsBoardAdmin(adminCheckRes.data.is_board_admin);
      // Set settings from board
      setTtcStartColumn(boardRes.data.ttc_start_column_id || '');
      setTtcEndColumn(boardRes.data.ttc_end_column_id || '');
      setBoardAdminUserId(boardRes.data.board_admin_user_id || '');
    } catch (error) {
      console.error('Failed to load board data:', error);
      toast.error('Failed to load board data');
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Global search with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      setGlobalSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/tickets/search/global`, { params: { q: searchQuery.trim() } });
        setGlobalSearchResults(res.data);
        setShowSearchResults(true);
      } catch (e) {
        console.error('Search failed:', e);
        setGlobalSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Board filter: unique creators and assignees
  const uniqueCreators = useMemo(() => {
    const map = new Map();
    tickets.forEach(t => { if (t.created_by && t.created_by_name) map.set(t.created_by, t.created_by_name); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [tickets]);

  const uniqueAssignees = useMemo(() => {
    const map = new Map();
    tickets.forEach(t => { if (t.assignee_user_id && t.assignee_name) map.set(t.assignee_user_id, t.assignee_name); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [tickets]);

  // Available options for the selected custom field filter
  const filterFieldOptions = useMemo(() => {
    if (filterFieldId === 'all') return [];
    const field = customFields.find(f => f.field_id === filterFieldId);
    return field ? field.options : [];
  }, [filterFieldId, customFields]);

  // Reset field value when field changes
  useEffect(() => {
    setFilterFieldValue('all');
  }, [filterFieldId]);

  // Filtered tickets for the board view
  const filteredTickets = useMemo(() => {
    let result = tickets;
    if (filterCreator !== 'all') {
      result = result.filter(t => t.created_by === filterCreator);
    }
    if (filterAssignee !== 'all') {
      result = result.filter(t => t.assignee_user_id === filterAssignee);
    }
    if (filterFieldId !== 'all' && filterFieldValue !== 'all') {
      result = result.filter(t => {
        const vals = t.custom_fields?.[filterFieldId];
        return vals && vals.includes(filterFieldValue);
      });
    }
    return result;
  }, [tickets, filterCreator, filterAssignee, filterFieldId, filterFieldValue]);

  const hasActiveFilters = filterCreator !== 'all' || filterAssignee !== 'all' || filterFieldId !== 'all';

  const handleSaveSettings = async () => {
    try {
      const updateData = { 
        ttc_start_column_id: ttcStartColumn || 'none', 
        ttc_end_column_id: ttcEndColumn || 'none' 
      };
      
      // Only super admin can change board admin
      if (isSuperAdmin) {
        updateData.board_admin_user_id = boardAdminUserId || 'none';
      }
      
      await api.patch(
        `/boards/${boardId}`,
        updateData,
        
      );
      setBoard({ 
        ...board, 
        ttc_start_column_id: ttcStartColumn, 
        ttc_end_column_id: ttcEndColumn,
        board_admin_user_id: boardAdminUserId
      });
      setShowSettingsModal(false);
      toast.success('Board settings saved');
      loadData(); // Reload to get updated admin name
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const handleAddCustomField = async () => {
    if (!newFieldName.trim()) {
      toast.error('Field name is required');
      return;
    }
    if (!newFieldOptions.trim()) {
      toast.error('At least one option is required');
      return;
    }

    const options = newFieldOptions.split(',').map(opt => {
      const trimmed = opt.trim();
      return { value: trimmed.toLowerCase().replace(/\s+/g, '_'), label: trimmed };
    }).filter(opt => opt.label);

    if (options.length === 0) {
      toast.error('At least one valid option is required');
      return;
    }

    try {
      await api.post(
        `/boards/${boardId}/custom-fields`,
        {
          name: newFieldName.trim(),
          options: options,
          is_mandatory: newFieldMandatory
        },
        
      );
      toast.success('Custom field added');
      setNewFieldName('');
      setNewFieldOptions('');
      setNewFieldMandatory(false);
      setShowAddField(false);
      loadData();
    } catch (error) {
      console.error('Failed to add custom field:', error);
      toast.error(error.response?.data?.detail || 'Failed to add custom field');
    }
  };

  const handleDeleteCustomField = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this custom field? This will not remove values from existing tickets.')) {
      return;
    }
    try {
      await api.delete(`/boards/${boardId}/custom-fields/${fieldId}`);
      toast.success('Custom field deleted');
      loadData();
    } catch (error) {
      console.error('Failed to delete custom field:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete custom field');
    }
  };

  const startEditField = (field) => {
    setEditingFieldId(field.field_id);
    setEditFieldName(field.name);
    setEditFieldOptions(field.options.map(o => o.label).join(', '));
    setEditFieldMandatory(field.is_mandatory);
  };

  const cancelEditField = () => {
    setEditingFieldId(null);
    setEditFieldName('');
    setEditFieldOptions('');
    setEditFieldMandatory(false);
  };

  const handleUpdateCustomField = async () => {
    if (!editFieldName.trim()) {
      toast.error('Field name is required');
      return;
    }
    if (!editFieldOptions.trim()) {
      toast.error('At least one option is required');
      return;
    }

    const options = editFieldOptions.split(',').map(opt => {
      const trimmed = opt.trim();
      return { value: trimmed.toLowerCase().replace(/\s+/g, '_'), label: trimmed };
    }).filter(opt => opt.label);

    if (options.length === 0) {
      toast.error('At least one valid option is required');
      return;
    }

    try {
      await api.put(
        `/boards/${boardId}/custom-fields/${editingFieldId}`,
        {
          name: editFieldName.trim(),
          options: options,
          is_mandatory: editFieldMandatory
        },
        
      );
      toast.success('Custom field updated');
      cancelEditField();
      loadData();
    } catch (error) {
      console.error('Failed to update custom field:', error);
      toast.error(error.response?.data?.detail || 'Failed to update custom field');
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    // Column drag - identified by 'col:' prefix
    if (typeof active.id === 'string' && active.id.startsWith('col:')) {
      return;
    }
    const ticket = tickets.find((t) => t.ticket_id === active.id);
    setActiveTicket(ticket);
    setOriginalColumnId(ticket?.column_id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    // Skip preview updates for column drags
    if (typeof active.id === 'string' && active.id.startsWith('col:')) return;

    const activeTicket = tickets.find((t) => t.ticket_id === active.id);
    const overColumn = columns.find((c) => c.column_id === over.id);

    if (overColumn && activeTicket && activeTicket.column_id !== overColumn.column_id) {
      setTickets((tickets) =>
        tickets.map((t) =>
          t.ticket_id === activeTicket.ticket_id ? { ...t, column_id: overColumn.column_id } : t
        )
      );
    }
  };

  const handleColumnReorder = async (fromColumnId, toColumnId) => {
    const oldIndex = columns.findIndex((c) => c.column_id === fromColumnId);
    const newIndex = columns.findIndex((c) => c.column_id === toColumnId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const previousColumns = columns;
    const reordered = arrayMove(columns, oldIndex, newIndex);
    // Optimistic update
    setColumns(reordered);

    try {
      await api.patch(`/boards/${boardId}/columns/reorder`, {
        column_ids: reordered.map((c) => c.column_id),
      });
      toast.success('Column order updated');
    } catch (error) {
      console.error('Failed to reorder columns:', error);
      toast.error(error.response?.data?.detail || 'Failed to reorder columns');
      setColumns(previousColumns);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    // Column drag
    if (typeof active.id === 'string' && active.id.startsWith('col:')) {
      if (over && typeof over.id === 'string' && over.id.startsWith('col:') && active.id !== over.id) {
        const fromColumnId = active.id.slice(4);
        const toColumnId = over.id.slice(4);
        await handleColumnReorder(fromColumnId, toColumnId);
      }
      return;
    }

    // Ticket drag
    setActiveTicket(null);

    if (!over) {
      setOriginalColumnId(null);
      return;
    }

    const ticket = tickets.find((t) => t.ticket_id === active.id);

    let targetColumn = columns.find((c) => c.column_id === over.id);

    if (!targetColumn) {
      const overTicket = tickets.find((t) => t.ticket_id === over.id);
      if (overTicket) {
        targetColumn = columns.find((c) => c.column_id === overTicket.column_id);
      }
    }

    if (!ticket || !targetColumn) {
      setOriginalColumnId(null);
      return;
    }

    if (originalColumnId && originalColumnId !== targetColumn.column_id) {
      try {
        const response = await api.patch(
          `/tickets/${ticket.ticket_id}`,
          { column_id: targetColumn.column_id },
          
        );
        // Move the updated ticket to the top of the target column
        const updatedTicket = response.data;
        setTickets(prev => [updatedTicket, ...prev.filter(t => t.ticket_id !== ticket.ticket_id)]);
        toast.success('Ticket moved successfully');
      } catch (error) {
        console.error('Failed to move ticket:', error);
        toast.error('Failed to move ticket');
        loadData();
      }
    }
    
    setOriginalColumnId(null);
  };

  const handleColumnNameUpdate = async (columnId, newName) => {
    try {
      await api.patch(
        `/columns/${columnId}`,
        { name: newName },
        
      );
      setColumns(columns.map((c) => (c.column_id === columnId ? { ...c, name: newName } : c)));
      toast.success('Column name updated');
    } catch (error) {
      console.error('Failed to update column name:', error);
      toast.error('Failed to update column name');
    }
  };

  const handleTicketCreated = (newTicket) => {
    setTickets([newTicket, ...tickets]);
    setShowCreateTicket(false);
    setNewColumnForTicket(null);
  };

  const handleTicketUpdated = (updatedTicket) => {
    setTickets(tickets.map((t) => (t.ticket_id === updatedTicket.ticket_id ? updatedTicket : t)));
    setSelectedTicket(null);
  };

  const handleTicketDeleted = (ticketId) => {
    setTickets(tickets.filter((t) => t.ticket_id !== ticketId));
    setSelectedTicket(null);
  };

  const handleColumnAdded = (newColumn) => {
    setColumns([...columns, newColumn]);
    setShowAddColumn(false);
  };

  const openCreateTicketForColumn = (columnId) => {
    setNewColumnForTicket(columnId);
    setShowCreateTicket(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="kanban-board">
      {/* Header */}
      <div className="border-b border-stone-200 bg-white px-6 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-stone-900">
              {board?.name || 'Board'}
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              {hasActiveFilters ? `${filteredTickets.length} of ${tickets.length}` : tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'}
              {board?.board_admin_name && (
                <span className="ml-2">• Admin: {board.board_admin_name}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400 w-4 h-4" strokeWidth={1.5} />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(e.target.value.length > 0);
                }}
                placeholder="Search all tickets..."
                className="pl-9 w-64 rounded-xl"
                data-testid="ticket-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
              
              {/* Search Results Dropdown */}
              {showSearchResults && globalSearchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto w-96">
                  {globalSearchResults.map((ticket) => (
                    <button
                      key={ticket.ticket_id}
                      onClick={() => {
                        setSelectedTicket(ticket);
                        setShowSearchResults(false);
                        setSearchQuery('');
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-stone-50 border-b border-stone-100 last:border-b-0"
                      data-testid={`search-result-${ticket.ticket_id}`}
                    >
                      <p className="font-medium text-stone-800 text-sm">{ticket.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-primary font-medium bg-primary/10 px-1.5 py-0.5 rounded">{ticket.board_name}</span>
                        <span className="text-xs text-stone-400">{ticket.column_name}</span>
                        {ticket.assignee_name && (
                          <span className="text-xs text-stone-500">• {ticket.assignee_name}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {showSearchResults && searchQuery && globalSearchResults.length === 0 && !isSearching && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-50 p-4 text-center text-stone-500 text-sm">
                  No tickets found
                </div>
              )}
              {isSearching && searchQuery && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-50 p-4 text-center text-stone-400 text-sm">
                  Searching...
                </div>
              )}
            </div>

            {isBoardAdmin && (
              <Button
                data-testid="board-settings-button"
                onClick={() => setShowSettingsModal(true)}
                variant="outline"
                className="bg-white border border-stone-200 hover:bg-stone-50 hover:border-primary/30 text-stone-700 rounded-xl"
              >
                <Settings className="w-4 h-4 mr-2" strokeWidth={2} />
                Settings
              </Button>
            )}
            <Button
              data-testid="board-analytics-button"
              onClick={() => navigate(`/board/${boardId}/analytics`)}
              variant="outline"
              className="bg-white border border-stone-200 hover:bg-stone-50 hover:border-primary/30 text-stone-700 rounded-xl"
            >
              <BarChart3 className="w-4 h-4 mr-2" strokeWidth={2} />
              Analytics
            </Button>
            <Button
              data-testid="add-column-button"
              onClick={() => setShowAddColumn(true)}
              variant="outline"
              className="bg-white border border-stone-200 hover:bg-stone-50 hover:border-primary/30 text-stone-700 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
              Add Column
            </Button>
            <Button
              data-testid="create-ticket-kanban-button"
              onClick={() => setShowCreateTicket(true)}
              className="bg-primary hover:bg-primary-hover text-stone-900 rounded-xl shadow-gold font-semibold transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
              New Ticket
            </Button>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="border-b border-stone-200 bg-white px-6 py-3">
        <div className="max-w-[1600px] mx-auto flex items-center gap-3 flex-wrap">
          <span className="text-xs font-medium text-stone-500 uppercase tracking-wider mr-1">Filters</span>
          
          <Select value={filterCreator} onValueChange={setFilterCreator}>
            <SelectTrigger className="w-44 h-8 text-xs rounded-lg" data-testid="filter-creator">
              <SelectValue placeholder="Creator" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Creators</SelectItem>
              {uniqueCreators.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterAssignee} onValueChange={setFilterAssignee}>
            <SelectTrigger className="w-44 h-8 text-xs rounded-lg" data-testid="filter-assignee">
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              {uniqueAssignees.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {customFields.length > 0 && (
            <>
              <Select value={filterFieldId} onValueChange={setFilterFieldId}>
                <SelectTrigger className="w-44 h-8 text-xs rounded-lg" data-testid="filter-field">
                  <SelectValue placeholder="Custom Field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Fields</SelectItem>
                  {customFields.map(f => (
                    <SelectItem key={f.field_id} value={f.field_id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {filterFieldId !== 'all' && filterFieldOptions.length > 0 && (
                <Select value={filterFieldValue} onValueChange={setFilterFieldValue}>
                  <SelectTrigger className="w-44 h-8 text-xs rounded-lg" data-testid="filter-field-value">
                    <SelectValue placeholder="Field Value" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Values</SelectItem>
                    {filterFieldOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </>
          )}

          {hasActiveFilters && (
            <button
              onClick={() => { setFilterCreator('all'); setFilterAssignee('all'); setFilterFieldId('all'); setFilterFieldValue('all'); }}
              className="text-xs text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1 ml-1"
              data-testid="clear-board-filters"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden bg-stone-50">
        <div className="max-w-[1600px] mx-auto p-6 h-full">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-4 h-[calc(100vh-15rem)]">
              <SortableContext
                items={columns.map((c) => `col:${c.column_id}`)}
                strategy={horizontalListSortingStrategy}
              >
                {columns.map((column, colIndex) => {
                  const columnTickets = filteredTickets.filter((t) => t.column_id === column.column_id);
                  const isStartColumn = column.column_id === board?.ttc_start_column_id;
                  const isEndColumn = column.column_id === board?.ttc_end_column_id;
                  const isLastColumn = colIndex === columns.length - 1;
                  return (
                    <KanbanColumn
                      key={column.column_id}
                      column={column}
                      tickets={columnTickets}
                      customFields={customFields}
                      onColumnNameUpdate={handleColumnNameUpdate}
                      onTicketClick={setSelectedTicket}
                      onAddTicket={() => openCreateTicketForColumn(column.column_id)}
                      isStartColumn={isStartColumn}
                      isEndColumn={isEndColumn}
                      isLastColumn={isLastColumn}
                      isBoardAdmin={isBoardAdmin || isSuperAdmin}
                    />
                  );
                })}
              </SortableContext>

              {columns.length === 0 && (
                <div className="flex-1 flex items-center justify-center text-stone-500">
                  <div className="text-center">
                    <p className="mb-4">No columns yet</p>
                    <Button onClick={() => setShowAddColumn(true)}>Add Your First Column</Button>
                  </div>
                </div>
              )}
            </div>

            <DragOverlay>
              {activeTicket ? <KanbanTicketCard ticket={activeTicket} customFields={customFields} isDragging /> : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {/* Board Settings Modal */}
      {showSettingsModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowSettingsModal(false);
          }}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full border border-stone-200 max-h-[90vh] flex flex-col" data-testid="board-settings-modal">
            <div className="flex items-center justify-between p-6 border-b border-stone-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" strokeWidth={2} />
                <h2 className="text-xl font-bold text-stone-900">Board Settings</h2>
              </div>
              <button onClick={() => setShowSettingsModal(false)} className="p-2 hover:bg-stone-100 rounded-xl">
                <X className="w-5 h-5 text-stone-500" strokeWidth={1.5} />
              </button>
            </div>
            
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Board Admin (Super Admin Only) */}
              {isSuperAdmin && (
                <div>
                  <h3 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" strokeWidth={1.5} />
                    Board Admin
                  </h3>
                  <Select value={boardAdminUserId || 'none'} onValueChange={(val) => setBoardAdminUserId(val === 'none' ? '' : val)}>
                    <SelectTrigger data-testid="board-admin-select">
                      <SelectValue placeholder="Select board admin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No admin assigned</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">Board admin can manage TTC settings and custom fields</p>
                </div>
              )}

              {/* TTC Configuration */}
              <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
                  <Timer className="w-4 h-4" strokeWidth={1.5} />
                  Time To Complete
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Start Column</label>
                    <Select value={ttcStartColumn || 'none'} onValueChange={(val) => setTtcStartColumn(val === 'none' ? '' : val)}>
                      <SelectTrigger data-testid="ttc-start-column-select">
                        <SelectValue placeholder="Select start column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {columns.map((col) => (
                          <SelectItem key={col.column_id} value={col.column_id}>
                            {col.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">End Column</label>
                    <Select value={ttcEndColumn || 'none'} onValueChange={(val) => setTtcEndColumn(val === 'none' ? '' : val)}>
                      <SelectTrigger data-testid="ttc-end-column-select">
                        <SelectValue placeholder="Select end column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {columns.map((col) => (
                          <SelectItem key={col.column_id} value={col.column_id}>
                            {col.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Custom Fields */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                    <ListPlus className="w-4 h-4" strokeWidth={1.5} />
                    Custom Fields
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddField(!showAddField)}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Field
                  </Button>
                </div>
                
                {/* Add Field Form */}
                {showAddField && (
                  <div className="bg-stone-50 rounded-lg p-4 mb-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Field Name</label>
                      <Input
                        value={newFieldName}
                        onChange={(e) => setNewFieldName(e.target.value)}
                        placeholder="e.g., Department"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1">Options (comma-separated)</label>
                      <Input
                        value={newFieldOptions}
                        onChange={(e) => setNewFieldOptions(e.target.value)}
                        placeholder="e.g., Engineering, Design, Marketing"
                        className="text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="mandatory"
                        checked={newFieldMandatory}
                        onCheckedChange={setNewFieldMandatory}
                      />
                      <label htmlFor="mandatory" className="text-xs text-stone-700">
                        Mandatory field
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleAddCustomField}>Save Field</Button>
                      <Button size="sm" variant="outline" onClick={() => setShowAddField(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
                
                {/* Existing Fields */}
                <div className="space-y-2">
                  {customFields.map((field) => (
                    <div key={field.field_id} className="p-3 bg-stone-50 rounded-lg">
                      {editingFieldId === field.field_id ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-stone-700 mb-1">Field Name</label>
                            <Input
                              value={editFieldName}
                              onChange={(e) => setEditFieldName(e.target.value)}
                              className="text-sm"
                              data-testid={`edit-field-name-${field.field_id}`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-stone-700 mb-1">Options (comma-separated)</label>
                            <Input
                              value={editFieldOptions}
                              onChange={(e) => setEditFieldOptions(e.target.value)}
                              className="text-sm"
                              data-testid={`edit-field-options-${field.field_id}`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id={`edit-mandatory-${field.field_id}`}
                              checked={editFieldMandatory}
                              onCheckedChange={setEditFieldMandatory}
                            />
                            <label htmlFor={`edit-mandatory-${field.field_id}`} className="text-xs text-stone-700">
                              Mandatory field
                            </label>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleUpdateCustomField} data-testid={`save-edit-field-${field.field_id}`}>Save</Button>
                            <Button size="sm" variant="outline" onClick={cancelEditField}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-stone-800">
                              {field.name}
                              {field.is_mandatory && <span className="text-rose-500 ml-1">*</span>}
                            </p>
                            <p className="text-xs text-stone-500">
                              {field.options.map(o => o.label).join(', ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => startEditField(field)}
                              className="p-1.5 hover:bg-blue-100 rounded transition-colors"
                              title="Edit field"
                              data-testid={`edit-field-btn-${field.field_id}`}
                            >
                              <Pencil className="w-4 h-4 text-blue-500" strokeWidth={1.5} />
                            </button>
                            <button
                              onClick={() => handleDeleteCustomField(field.field_id)}
                              className="p-1.5 hover:bg-rose-100 rounded transition-colors"
                              title="Delete field"
                              data-testid={`delete-field-btn-${field.field_id}`}
                            >
                              <Trash2 className="w-4 h-4 text-rose-500" strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {customFields.length === 0 && (
                    <p className="text-xs text-stone-500 text-center py-2">No custom fields yet</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-stone-200 flex justify-end gap-3 flex-shrink-0">
              <Button variant="outline" onClick={() => setShowSettingsModal(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button
                onClick={handleSaveSettings}
                className="bg-primary hover:bg-primary-hover text-stone-900 rounded-xl shadow-gold font-semibold"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCreateTicket && (
        <CreateTicketModal
          boards={boards}
          defaultBoardId={boardId}
          defaultColumnId={newColumnForTicket}
          customFields={customFields}
          onClose={() => {
            setShowCreateTicket(false);
            setNewColumnForTicket(null);
          }}
          onTicketCreated={handleTicketCreated}
        />
      )}

      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          boards={boards}
          customFields={customFields}
          onClose={() => setSelectedTicket(null)}
          onTicketUpdated={handleTicketUpdated}
          onTicketDeleted={handleTicketDeleted}
        />
      )}

      {showAddColumn && (
        <AddColumnModal
          boardId={boardId}
          onClose={() => setShowAddColumn(false)}
          onColumnAdded={handleColumnAdded}
        />
      )}
    </div>
  );
}

export default KanbanBoard;
